import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Users, DollarSign, Monitor, UserCog, Ban, SlidersHorizontal, History, Hash, AlertTriangle } from 'lucide-react'
import { Drawer } from 'vaul'
import { cn, formatCurrency } from '@/lib/utils'
import { fadeInUp, cardReveal } from '@/lib/motion'

type AgentStatus = 'active' | 'suspended'
type ConfirmAction = null | 'suspend' | 'cap'

type Agent = {
  id: string; name: string; code: string; status: AgentStatus
  rooms: number; referrals: number; monthCommission: number; totalCommission: number; rate: number; cap: number
}

const initialAgents: Agent[] = [
  { id: 'AG01', name: 'Sarah_HK', code: 'SARAH1', status: 'active', rooms: 2, referrals: 34, monthCommission: 128500, totalCommission: 423000, rate: 2.5, cap: 5 },
  { id: 'AG02', name: 'PokerKing', code: 'PKING1', status: 'active', rooms: 3, referrals: 56, monthCommission: 215000, totalCommission: 892000, rate: 3.0, cap: 5 },
  { id: 'AG03', name: '\u5927\u54E5', code: 'DAGE01', status: 'active', rooms: 0, referrals: 12, monthCommission: 0, totalCommission: 45000, rate: 2.0, cap: 5 },
  { id: 'AG04', name: 'VIP_Host', code: 'VIPH01', status: 'suspended', rooms: 0, referrals: 8, monthCommission: 0, totalCommission: 67000, rate: 2.5, cap: 5 },
  { id: 'AG05', name: 'TW_Agent', code: 'TWAG01', status: 'active', rooms: 1, referrals: 23, monthCommission: 87500, totalCommission: 312000, rate: 2.5, cap: 5 },
]

const avatarGradients = [
  'from-amber-500 to-orange-600',
  'from-violet-500 to-purple-600',
  'from-cyan-500 to-blue-600',
  'from-rose-500 to-pink-600',
  'from-emerald-500 to-teal-600',
]

const mockRooms = [
  { id: 'R-101', stakes: '1/2', players: 5, earnings: 64000 },
  { id: 'R-102', stakes: '5/10', players: 3, earnings: 151000 },
  { id: 'R-205', stakes: '2/5', players: 7, earnings: 98000 },
]

function Avatar({ name, index }: { name: string; index: number }) {
  const initials = name.slice(0, 2).toUpperCase()
  return (
    <div className={cn('w-10 h-10 rounded-full bg-gradient-to-br flex items-center justify-center flex-shrink-0', avatarGradients[index % avatarGradients.length])}>
      <span className="text-xs font-bold text-white">{initials}</span>
    </div>
  )
}

export function AdminAgentsScreen() {
  const navigate = useNavigate()
  const [agents, setAgents] = useState<Agent[]>(initialAgents)
  const [selected, setSelected] = useState<Agent | null>(null)
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null)
  const [suspendReason, setSuspendReason] = useState('')
  const [newCap, setNewCap] = useState(5)

  const resetConfirmState = () => {
    setConfirmAction(null)
    setSuspendReason('')
    setNewCap(selected?.cap ?? 5)
  }

  const closeDrawer = () => {
    setSelected(null)
    resetConfirmState()
  }

  const handleSuspendConfirm = () => {
    if (!selected || !suspendReason.trim()) return
    setAgents((prev) =>
      prev.map((a) =>
        a.id === selected.id ? { ...a, status: 'suspended' as AgentStatus } : a
      )
    )
    closeDrawer()
  }

  const handleCapConfirm = () => {
    if (!selected) return
    setAgents((prev) =>
      prev.map((a) =>
        a.id === selected.id ? { ...a, cap: newCap } : a
      )
    )
    closeDrawer()
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
      className="p-4 pb-8 flex flex-col gap-5"
    >
      {/* Summary Stats */}
      <motion.div variants={fadeInUp} className="grid grid-cols-3 gap-3">
        {[
          { icon: UserCog, label: '\u6D3B\u8E8D\u4EE3\u7406', value: String(agents.filter((a) => a.status === 'active').length), color: 'text-emerald-400 bg-emerald-500/10' },
          { icon: DollarSign, label: '\u7E3D\u4F63\u91D1', value: formatCurrency(agents.reduce((sum, a) => sum + a.totalCommission, 0)), color: 'text-gold bg-gold/10' },
          { icon: Monitor, label: '\u4EE3\u7406\u623F\u9593', value: String(agents.reduce((sum, a) => sum + a.rooms, 0)), color: 'text-blue-400 bg-blue-500/10' },
        ].map((s) => (
          <motion.div key={s.label} variants={cardReveal} className="p-3.5 rounded-2xl glass-card space-y-2">
            <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center', s.color)}>
              <s.icon className="w-4 h-4" />
            </div>
            <div>
              <p className="text-lg font-bold font-mono text-foreground">{s.value}</p>
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Agent List */}
      <motion.section variants={fadeInUp} className="space-y-2">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">{'\u4EE3\u7406\u5217\u8868'}</h3>
        {agents.map((agent, i) => (
          <motion.button
            key={agent.id}
            variants={cardReveal}
            onClick={() => { setSelected(agent); resetConfirmState() }}
            className="w-full text-left p-4 rounded-2xl glass-card space-y-3 active:scale-[0.98] transition-transform"
          >
            {/* Header row */}
            <div className="flex items-center gap-3">
              <Avatar name={agent.name} index={i} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-foreground truncate">{agent.name}</p>
                  <span className={cn(
                    'px-2 py-0.5 rounded-md text-[10px] font-bold uppercase',
                    agent.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                  )}>
                    {agent.status === 'active' ? '\u6D3B\u8E8D' : '\u505C\u7528'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Hash className="w-3 h-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground font-mono">{agent.code}</span>
                </div>
              </div>
            </div>
            {/* Stats row */}
            <div className="grid grid-cols-3 gap-2">
              <div className="p-2 rounded-xl bg-surface-elevated text-center">
                <p className="text-[10px] text-muted-foreground">{'\u623F\u9593'}</p>
                <p className="text-sm font-bold text-foreground font-mono">{agent.rooms}</p>
              </div>
              <div className="p-2 rounded-xl bg-surface-elevated text-center">
                <p className="text-[10px] text-muted-foreground">{'\u63A8\u85A6\u73A9\u5BB6'}</p>
                <p className="text-sm font-bold text-foreground font-mono">{agent.referrals}</p>
              </div>
              <div className="p-2 rounded-xl bg-surface-elevated text-center">
                <p className="text-[10px] text-muted-foreground">{'\u7E3D\u4F63\u91D1'}</p>
                <p className="text-sm font-bold text-gold font-mono">{formatCurrency(agent.totalCommission)}</p>
              </div>
            </div>
            {/* Cap indicator */}
            <div className="flex items-center justify-between border-t border-foreground/10 pt-2">
              <span className="text-[10px] text-muted-foreground">{'\u4F63\u91D1\u4E0A\u9650'}: {agent.cap}%</span>
              <span className="text-[10px] text-muted-foreground">{'\u7576\u524D\u8CBB\u7387'}: {agent.rate}%</span>
            </div>
          </motion.button>
        ))}
      </motion.section>

      {/* Agent Detail Drawer */}
      <Drawer.Root open={selected !== null} onOpenChange={(open) => { if (!open) closeDrawer() }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none max-h-[85vh]">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 overflow-y-auto max-h-[85vh] scrollbar-hide">
              <div className="flex justify-center py-3 sticky top-0 bg-surface-elevated z-10">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>
              {selected && (
                <div className="px-5 pb-8 safe-area-bottom space-y-5">
                  <Drawer.Title className="sr-only">{selected.name} {'\u4EE3\u7406\u8A73\u60C5'}</Drawer.Title>

                  <AnimatePresence mode="wait">
                    {/* Suspend Confirmation */}
                    {confirmAction === 'suspend' && (
                      <motion.div
                        key="suspend"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                      >
                        <div className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 space-y-2">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-rose-400" />
                            <h3 className="text-base font-bold text-rose-400">確定要停用此代理？</h3>
                          </div>
                          <p className="text-xs text-rose-300/80">此操作將影響該代理的所有活躍房間，房間內的玩家將會被通知</p>
                          <p className="text-xs text-muted-foreground">代理: {selected.name} ({selected.code}) | 活躍房間: {selected.rooms}</p>
                        </div>

                        <div className="space-y-2">
                          <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider">停用原因 *</label>
                          <textarea
                            value={suspendReason}
                            onChange={(e) => setSuspendReason(e.target.value)}
                            placeholder="請輸入停用原因..."
                            className="w-full h-24 p-3 rounded-xl bg-surface-elevated border border-foreground/10 text-sm text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:border-rose-500/40"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={resetConfirmState}
                            className="h-12 glass-card rounded-2xl font-semibold text-foreground"
                          >
                            取消
                          </button>
                          <button
                            onClick={handleSuspendConfirm}
                            disabled={!suspendReason.trim()}
                            className={cn(
                              'h-12 rounded-2xl font-bold text-white transition-opacity',
                              suspendReason.trim() ? 'bg-rose-600' : 'bg-rose-600/40 opacity-50 cursor-not-allowed'
                            )}
                          >
                            確認停用
                          </button>
                        </div>
                      </motion.div>
                    )}

                    {/* Cap Adjustment */}
                    {confirmAction === 'cap' && (
                      <motion.div
                        key="cap"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-4"
                      >
                        <h3 className="text-lg font-bold text-foreground">調整佣金上限</h3>
                        <p className="text-xs text-muted-foreground">
                          代理: {selected.name} | 當前上限: {selected.cap}% | 當前費率: {selected.rate}%
                        </p>

                        {/* Current vs New display */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="p-3 rounded-xl bg-surface-elevated text-center">
                            <p className="text-[10px] text-muted-foreground mb-1">當前上限</p>
                            <p className="text-xl font-bold font-mono text-muted-foreground">{selected.cap}%</p>
                          </div>
                          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-center">
                            <p className="text-[10px] text-amber-400 mb-1">新上限</p>
                            <p className="text-xl font-bold font-mono text-amber-400">{newCap}%</p>
                          </div>
                        </div>

                        {/* Slider */}
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider">佣金上限</label>
                            <span className="text-sm font-bold font-mono text-amber-400">{newCap}%</span>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={10}
                            step={0.5}
                            value={newCap}
                            onChange={(e) => setNewCap(parseFloat(e.target.value))}
                            className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-amber-400 bg-surface-elevated"
                          />
                          <div className="flex justify-between text-[10px] text-muted-foreground">
                            <span>0%</span>
                            <span>5%</span>
                            <span>10%</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={resetConfirmState}
                            className="h-12 glass-card rounded-2xl font-semibold text-foreground"
                          >
                            取消
                          </button>
                          <button
                            onClick={handleCapConfirm}
                            className="h-12 rounded-2xl font-bold text-white bg-amber-600"
                          >
                            確認調整
                          </button>
                        </div>
                      </motion.div>
                    )}

                    {/* Default Content */}
                    {confirmAction === null && (
                      <motion.div
                        key="default"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-5"
                      >
                        {/* Agent Header */}
                        <div className="flex items-center gap-4">
                          <Avatar name={selected.name} index={agents.findIndex((a) => a.id === selected.id)} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h2 className="text-lg font-bold text-foreground">{selected.name}</h2>
                              <span className={cn(
                                'px-2 py-0.5 rounded-md text-[10px] font-bold',
                                selected.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                              )}>
                                {selected.status === 'active' ? '\u6D3B\u8E8D' : '\u505C\u7528'}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">{'\u52A0\u5165\u65E5\u671F'}: 2025-09-15</p>
                          </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { label: '\u6D3B\u8E8D\u623F\u9593', value: String(selected.rooms) },
                            { label: '\u7E3D\u63A8\u85A6', value: String(selected.referrals) },
                            { label: '\u672C\u6708\u4F63\u91D1', value: formatCurrency(selected.monthCommission), highlight: true },
                            { label: '\u7E3D\u4F63\u91D1', value: formatCurrency(selected.totalCommission), highlight: true },
                            { label: '\u4F63\u91D1\u8CBB\u7387', value: `${selected.rate}%` },
                            { label: '\u4E0A\u9650', value: `${selected.cap}%` },
                          ].map((s) => (
                            <div key={s.label} className="p-3 rounded-xl bg-surface-elevated text-center">
                              <p className="text-[10px] text-muted-foreground mb-1">{s.label}</p>
                              <p className={cn('text-sm font-bold font-mono', s.highlight ? 'text-gold' : 'text-foreground')}>{s.value}</p>
                            </div>
                          ))}
                        </div>

                        {/* Room List */}
                        {selected.rooms > 0 && (
                          <div className="space-y-2">
                            <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{'\u6D3B\u8E8D\u623F\u9593'}</h4>
                            {mockRooms.slice(0, selected.rooms).map((room) => (
                              <div key={room.id} className="flex items-center justify-between p-3 rounded-xl glass-card">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-xl bg-surface-elevated flex flex-col items-center justify-center border border-foreground/5">
                                    <span className="text-[9px] text-muted-foreground">{'\u76F2\u6CE8'}</span>
                                    <span className="text-xs font-bold text-gold font-mono">${room.stakes}</span>
                                  </div>
                                  <div>
                                    <p className="text-sm font-bold text-foreground">{room.id}</p>
                                    <p className="text-[10px] text-muted-foreground"><Users className="w-3 h-3 inline mr-1" />{room.players} {'\u73A9\u5BB6'}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-bold font-mono text-gold">{formatCurrency(room.earnings)}</p>
                                  <p className="text-[10px] text-muted-foreground">{'\u4F63\u91D1\u6536\u5165'}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="grid grid-cols-3 gap-2 pt-2">
                          <button
                            onClick={() => setConfirmAction('suspend')}
                            className="flex flex-col items-center gap-2 p-3 rounded-xl glass-card active:scale-95 transition-transform"
                          >
                            <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                              <Ban className="w-5 h-5 text-red-400" />
                            </div>
                            <span className="text-[10px] font-bold text-red-400">{'\u505C\u7528\u4EE3\u7406'}</span>
                          </button>
                          <button
                            onClick={() => { setNewCap(selected.cap); setConfirmAction('cap') }}
                            className="flex flex-col items-center gap-2 p-3 rounded-xl glass-card active:scale-95 transition-transform"
                          >
                            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                              <SlidersHorizontal className="w-5 h-5 text-amber-400" />
                            </div>
                            <span className="text-[10px] font-bold text-amber-400">{'\u8ABF\u6574\u4F63\u91D1\u4E0A\u9650'}</span>
                          </button>
                          <button
                            onClick={() => { closeDrawer(); navigate('/admin/audit') }}
                            className="flex flex-col items-center gap-2 p-3 rounded-xl glass-card active:scale-95 transition-transform"
                          >
                            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                              <History className="w-5 h-5 text-blue-400" />
                            </div>
                            <span className="text-[10px] font-bold text-blue-400">{'\u67E5\u770B\u7D00\u9304'}</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
